/**
 * Property Form Page
 * Create (/customers/:customerId/properties/new) and edit
 * (/customers/:customerId/properties/:propertyId/edit) a customer's property.
 *
 * Only users allowed to manage properties may use this page; other roles see
 * a permission notice.
 */

import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { createAPIClient, IDispatchApi, IPropertiesApi, ITaxRatesApi } from '../api';
import * as types from '../api/types';
import { getErrorMessage } from '../utils/apiError';
import { canManageProperties } from '../utils/permissions';
import { formatRatePercent } from '../utils/taxRate';
import {
  SIZE_DIMENSION_LABELS,
  SIZE_UNIT_OPTIONS,
  propertySizeChanged,
  propertySizeFields,
  validatePropertySize,
} from '../utils/priceChart';
import { inputClass, labelClass, primaryButtonClass, secondaryButtonClass } from '../components/formStyles';
import { LeadSourceSelect } from '../components/LeadSourceSelect';
import { Toast } from '../components/Toast';
import { RoutePropagationDialog, RouteSelect, useDispatchRoutes } from '../components/RouteControls';
import { routeName } from './routeUi';

interface FormState {
  address: string;
  city: string;
  state: string;
  zip: string;
  taxRateId: string;
  leadSourceId: string | null;
  /** '' with sizeUnit '' means no size. */
  sizeValue: string;
  sizeUnit: string;
  /** Default dispatch route; '' = none (tracker item 57) */
  routeId: string;
}

const EMPTY_FORM: FormState = {
  address: '',
  city: '',
  state: '',
  zip: '',
  taxRateId: '',
  leadSourceId: null,
  sizeValue: '',
  sizeUnit: '',
  routeId: '',
};

function toFormState(property: types.Property): FormState {
  return {
    address: property.address ?? '',
    city: property.city ?? '',
    state: property.state ?? '',
    zip: property.zip ?? '',
    taxRateId: property.taxRateId ?? '',
    leadSourceId: property.leadSourceId ?? null,
    sizeValue: property.sizeValue ?? '',
    sizeUnit: property.sizeUnit ?? '',
    routeId: property.routeId ?? '',
  };
}

/** Backend requires all fields on create and rejects empty strings on update. */
function validate(form: FormState): string | null {
  if (!form.address.trim()) return 'Street address is required';
  if (!form.city.trim()) return 'City is required';
  if (!form.state.trim()) return 'State is required';
  if (!form.zip.trim()) return 'ZIP is required';
  if (!form.taxRateId) return 'Select a tax rate';
  return validatePropertySize(form.sizeValue, form.sizeUnit);
}

const SIZE_UNIT_GROUPS = (['AREA', 'LENGTH'] as const).map((dimension) => ({
  dimension,
  units: SIZE_UNIT_OPTIONS.filter((u) => u.dimension === dimension),
}));

interface TextFieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  disabled: boolean;
  required?: boolean;
  placeholder?: string;
  autoComplete?: string;
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
  className?: string;
}

function TextField({
  id,
  label,
  value,
  onChange,
  disabled,
  required,
  placeholder,
  autoComplete,
  inputMode,
  className,
}: TextFieldProps) {
  return (
    <div className={className}>
      <label htmlFor={id} className={labelClass}>
        {label}
        {required && <span className="ml-0.5 text-red-600">*</span>}
      </label>
      <input
        id={id}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        required={required}
        placeholder={placeholder}
        autoComplete={autoComplete}
        inputMode={inputMode}
        className={inputClass}
      />
    </div>
  );
}

interface FormSectionProps {
  title: string;
  description?: string;
  children: React.ReactNode;
}

function FormSection({ title, description, children }: FormSectionProps) {
  return (
    <div className="grid grid-cols-1 gap-x-8 gap-y-4 py-6 first:pt-0 last:pb-0 md:grid-cols-3">
      <div>
        <h2 className="text-base font-semibold text-gray-900">{title}</h2>
        {description && <p className="mt-1 text-sm text-gray-500">{description}</p>}
      </div>
      <div className="md:col-span-2">{children}</div>
    </div>
  );
}

export function PropertyFormPage() {
  const navigate = useNavigate();
  const { customerId, propertyId } = useParams<{ customerId: string; propertyId: string }>();
  const isEdit = Boolean(propertyId);
  const { user } = useAuth();
  const apiClient = React.useMemo<IPropertiesApi & ITaxRatesApi & IDispatchApi>(() => createAPIClient(), []);
  const canManage = canManageProperties(user);
  const { routes, error: routesError } = useDispatchRoutes(apiClient, canManage);

  // State
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [property, setProperty] = useState<types.Property | null>(null);
  const [taxRates, setTaxRates] = useState<types.TaxRate[]>([]);
  const [taxRatesError, setTaxRatesError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [routePrompt, setRoutePrompt] = useState(false);

  // Load tax rates, plus the existing property in edit mode
  useEffect(() => {
    if (!customerId || !canManage) {
      setIsLoading(false);
      return;
    }

    const loadData = async () => {
      try {
        setIsLoading(true);
        setLoadError(null);
        setTaxRatesError(null);

        // A tax rate failure is shown next to the select rather than hiding the form.
        const [taxRatesResult, propertyResult] = await Promise.allSettled([
          apiClient.listTaxRates(),
          propertyId ? apiClient.getProperty(propertyId) : Promise.resolve(null),
        ]);

        const rates = taxRatesResult.status === 'fulfilled' ? taxRatesResult.value : [];
        setTaxRates(rates);
        if (taxRatesResult.status === 'rejected') {
          setTaxRatesError(getErrorMessage(taxRatesResult.reason, 'Failed to load tax rates'));
        }

        if (propertyResult.status === 'rejected') {
          throw propertyResult.reason;
        }

        const loaded = propertyResult.value;
        if (loaded) {
          if (loaded.customerId !== customerId) {
            setLoadError('Property not found for this customer');
            return;
          }
          setProperty(loaded);
          setForm(toFormState(loaded));
        } else {
          // Preselect when there is only one choice (common for single-state businesses).
          setForm({ ...EMPTY_FORM, taxRateId: rates.length === 1 ? rates[0].id : '' });
        }
      } catch (err) {
        setLoadError(getErrorMessage(err, 'Failed to load property'));
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [apiClient, customerId, propertyId, canManage]);

  // Editing returns to the property's detail page; creating to the customer
  const cancelTarget = propertyId ? `/customers/${customerId}/properties/${propertyId}` : `/customers/${customerId}`;

  const setField = <K extends keyof FormState>(field: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const savedRouteId = property?.routeId ?? null;
  const routeChanged = isEdit && (form.routeId || null) !== savedRouteId;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!customerId) return;

    const validationError = validate(form);
    if (validationError) {
      setError(validationError);
      return;
    }
    // A changed default on an existing property asks what happens to its jobs first.
    if (routeChanged) {
      setError(null);
      setRoutePrompt(true);
      return;
    }
    await save();
  };

  const save = async (routePropagation?: types.RoutePropagation) => {
    if (!customerId) return;

    // The property is always owned by and billed to this customer; the
    // billing customer is intentionally not user-editable here.
    const fields = {
      address: form.address.trim(),
      city: form.city.trim(),
      state: form.state.trim(),
      zip: form.zip.trim(),
      taxRateId: form.taxRateId,
      billingCustomerId: customerId,
      leadSourceId: form.leadSourceId,
    };

    // Size: both-or-neither. Sent on create only when set, and on update only
    // when changed (clearing both sends null/null).
    const size = propertySizeFields(form.sizeValue, form.sizeUnit);

    try {
      setIsSaving(true);
      setError(null);

      if (propertyId) {
        // routeId is never sent on update: setRouteDefault changes it below.
        const sizeChanged = property ? propertySizeChanged(form.sizeValue, form.sizeUnit, property) : true;
        await apiClient.updateProperty(propertyId, { ...fields, ...(sizeChanged ? size : {}) });
        if (routePropagation) {
          try {
            await apiClient.setRouteDefault('property', propertyId, { routeId: form.routeId || null, routePropagation });
          } catch (err) {
            setRoutePrompt(false);
            setError(`The other changes were saved, but the default route was not changed: ${getErrorMessage(err, 'Could not change the default route')}`);
            return;
          }
        }
      } else {
        await apiClient.createProperty({ ...fields, customerId, ...(size.sizeValue !== null ? size : {}), ...(form.routeId ? { routeId: form.routeId } : {}) });
      }

      navigate(cancelTarget, { replace: true });
    } catch (err) {
      setRoutePrompt(false);
      setError(getErrorMessage(err, isEdit ? 'Failed to update property' : 'Failed to create property'));
    } finally {
      setIsSaving(false);
    }
  };

  // Keep the property's current rate selectable even if it is missing from the list.
  const taxRateOptions =
    property?.taxRate && !taxRates.some((r) => r.id === property.taxRateId)
      ? [...taxRates.map((r) => ({ id: r.id, name: r.name, rate: r.rate })), property.taxRate]
      : taxRates.map((r) => ({ id: r.id, name: r.name, rate: r.rate }));

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
      {/* Title */}
      <div className="mb-6">
        <button
          onClick={() => navigate(cancelTarget)}
          className="inline-flex items-center min-h-[44px] sm:min-h-0 text-blue-600 hover:text-blue-700 text-sm font-semibold"
        >
          {isEdit ? '← Property' : '← Customer'}
        </button>
        <h1 className="text-2xl font-bold text-gray-900 mt-1">
          {isEdit ? 'Edit Property' : 'New Property'}
        </h1>
      </div>

      {!canManage ? (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-red-800">
            You do not have permission to {isEdit ? 'edit' : 'create'} properties.
          </p>
        </div>
      ) : isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      ) : loadError ? (
        <>
          <Toast kind="error">{loadError}</Toast>
          <p className="mb-6 text-gray-700">The property could not be loaded.</p>
        </>
      ) : (
        <section className="bg-white rounded-lg shadow max-w-4xl">
          <form onSubmit={handleSubmit} noValidate>
            <div className="p-4 sm:p-8">
              {error && <Toast kind="error">{error}</Toast>}

              <div className="divide-y divide-gray-200">
                {/* Address */}
                <FormSection title="Address" description="Where the work is performed.">
                  <fieldset className="rounded-lg border border-gray-200 bg-gray-50 p-4 sm:p-5">
                    <legend className="sr-only">Address</legend>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-6">
                      <TextField
                        id="address"
                        label="Street address"
                        value={form.address}
                        onChange={(v) => setField('address', v)}
                        disabled={isSaving}
                        required
                        autoComplete="street-address"
                        placeholder="123 Main St"
                        className="sm:col-span-6"
                      />
                      <TextField
                        id="city"
                        label="City"
                        value={form.city}
                        onChange={(v) => setField('city', v)}
                        disabled={isSaving}
                        required
                        autoComplete="address-level2"
                        className="sm:col-span-3"
                      />
                      <TextField
                        id="state"
                        label="State"
                        value={form.state}
                        onChange={(v) => setField('state', v)}
                        disabled={isSaving}
                        required
                        autoComplete="address-level1"
                        className="sm:col-span-1"
                      />
                      <TextField
                        id="zip"
                        label="ZIP"
                        value={form.zip}
                        onChange={(v) => setField('zip', v)}
                        disabled={isSaving}
                        required
                        autoComplete="postal-code"
                        inputMode="numeric"
                        className="sm:col-span-2"
                      />
                    </div>
                  </fieldset>
                </FormSection>

                {/* Size */}
                <FormSection
                  title="Size"
                  description="Used by price charts to price services automatically. Leave blank if not needed."
                >
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label htmlFor="sizeValue" className={labelClass}>
                        Size
                        <span className="ml-1.5 text-xs font-normal text-gray-400">Optional</span>
                      </label>
                      <input
                        id="sizeValue"
                        type="text"
                        inputMode="decimal"
                        value={form.sizeValue}
                        onChange={(e) => setField('sizeValue', e.target.value)}
                        disabled={isSaving}
                        placeholder="5000"
                        className={inputClass}
                      />
                    </div>
                    <div>
                      <label htmlFor="sizeUnit" className={labelClass}>
                        Unit
                      </label>
                      <select
                        id="sizeUnit"
                        value={form.sizeUnit}
                        onChange={(e) => setField('sizeUnit', e.target.value)}
                        disabled={isSaving}
                        className={inputClass}
                      >
                        <option value="">No size</option>
                        {SIZE_UNIT_GROUPS.map((group) => (
                          <optgroup key={group.dimension} label={SIZE_DIMENSION_LABELS[group.dimension]}>
                            {group.units.map((u) => (
                              <option key={u.unit} value={u.unit}>
                                {u.label}
                              </option>
                            ))}
                          </optgroup>
                        ))}
                      </select>
                    </div>
                    {(form.sizeValue || form.sizeUnit) && (
                      <div className="sm:col-span-2">
                        <button
                          type="button"
                          onClick={() => setForm((prev) => ({ ...prev, sizeValue: '', sizeUnit: '' }))}
                          disabled={isSaving}
                          className="min-h-[44px] sm:min-h-0 text-sm font-semibold text-blue-600 hover:text-blue-700"
                        >
                          Clear size
                        </button>
                      </div>
                    )}
                  </div>
                </FormSection>

                {/* Lead Source */}
                <FormSection title="Lead Source" description="How the work at this property was won.">
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div className="sm:col-span-2">
                      <label htmlFor="leadSourceId" className={labelClass}>
                        Lead source
                        <span className="ml-1.5 text-xs font-normal text-gray-400">Optional</span>
                      </label>
                      <LeadSourceSelect
                        id="leadSourceId"
                        value={form.leadSourceId}
                        onChange={(v) => setField('leadSourceId', v)}
                        currentId={property?.leadSourceId ?? null}
                        disabled={isSaving}
                      />
                    </div>
                  </div>
                </FormSection>

                {/* Dispatch route (tracker item 57) */}
                <FormSection title="Dispatch" description="Default route for new jobs here. It wins over the customer's default route.">
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <RouteSelect
                      id="routeId"
                      label="Default route"
                      value={form.routeId}
                      onChange={(v) => setField('routeId', v)}
                      routes={routes}
                      currentId={savedRouteId}
                      leadingOptions={[{ value: '', label: 'No default route' }]}
                      disabled={isSaving}
                      loadError={routesError}
                      hint={isEdit ? 'Saving a different route asks what to do with existing jobs.' : undefined}
                    />
                  </div>
                </FormSection>

                {/* Tax */}
                <FormSection title="Tax" description="Sales tax applied to work at this property.">
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label htmlFor="taxRateId" className={labelClass}>
                        Tax rate<span className="ml-0.5 text-red-600">*</span>
                      </label>
                      <select
                        id="taxRateId"
                        value={form.taxRateId}
                        onChange={(e) => setField('taxRateId', e.target.value)}
                        disabled={isSaving}
                        className={inputClass}
                      >
                        <option value="">Select a tax rate</option>
                        {taxRateOptions.map((rate) => (
                          <option key={rate.id} value={rate.id}>
                            {rate.name} ({formatRatePercent(rate.rate)})
                          </option>
                        ))}
                      </select>
                      {taxRatesError ? (
                        <Toast kind="error">{taxRatesError}</Toast>
                      ) : (
                        taxRates.length === 0 && (
                          <p className="mt-1.5 text-xs text-gray-500">
                            No tax rates yet.{' '}
                            <Link to="/settings/tax-rates" className="font-semibold text-blue-600 hover:text-blue-700">
                              Set up tax rates
                            </Link>{' '}
                            first.
                          </p>
                        )
                      )}
                    </div>
                  </div>
                </FormSection>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-col-reverse gap-3 rounded-b-lg border-t border-gray-200 bg-gray-50 px-4 py-4 sm:flex-row sm:justify-end sm:px-8">
              <button
                type="button"
                onClick={() => navigate(cancelTarget)}
                disabled={isSaving}
                className={`${secondaryButtonClass} w-full sm:w-auto py-2.5 sm:py-2`}
              >
                Cancel
              </button>
              <button type="submit" disabled={isSaving} className={`${primaryButtonClass} w-full sm:w-auto py-2.5 sm:py-2`}>
                {isSaving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Property'}
              </button>
            </div>
          </form>
          {routePrompt && (
            <RoutePropagationDialog
              entity="property"
              oldName={savedRouteId ? routeName(routes, savedRouteId) : null}
              newName={form.routeId ? routeName(routes, form.routeId) : null}
              busy={isSaving}
              onConfirm={(choice) => void save(choice)}
              onCancel={() => setRoutePrompt(false)}
            />
          )}
        </section>
      )}
    </main>
  );
}
